import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap, throwIfEmpty } from 'rxjs/operators';
import { AppEventService } from '../../core/event/app-event.service';

/**
 * Key of the message shown when a request was swallowed before it ever reached the server.
 *
 * <p>Carried on the synthesized error so the existing listener in AppMainComponent renders it -
 * that listener only raises a toast when the body has an errorKey.</p>
 */
export const SESSION_EXPIRED_ERROR_KEY = 'error.session.expired';

export const errorHandlerInterceptor: HttpInterceptorFn = (req, next) => {
  const eventService = inject(AppEventService);

  return next(req).pipe(
    // A request that finishes without a response never happened. MsalInterceptor does exactly that
    // when a silent token refresh fails: it starts an interactive redirect and returns EMPTY, which
    // completes without emitting. The caller then sees neither a result nor an error and waits for
    // ever. Turning it into a real error is what gives every caller something to react to.
    //
    // This interceptor is registered via withInterceptors(), which runs ahead of the DI-registered
    // MsalInterceptor, so next(req) here already covers it. A genuine HTTP response always emits,
    // so nothing else can trip this.
    throwIfEmpty(() => new HttpErrorResponse({
      status: 0,
      statusText: 'No response - the request was cancelled before it reached the server',
      url: req.url,
      error: { errorKey: SESSION_EXPIRED_ERROR_KEY },
    })),
    tap({
      error: (err: any) => {
        if (err instanceof HttpErrorResponse) {
          if (!(err.status === 401 && (err.message === '' || (err.url && err.url.includes('api/account'))))) {
            eventService.broadcast({ name: 'leanbyteApp.httpError', content: err });
          }
        }
      }
    })
  );
};
